import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { History, MapPin, CheckCircle2, CircleDashed } from 'lucide-react';
import { toast } from 'sonner';
import ChangeHistoryDialog from './ChangeHistoryDialog';

const API = getBackendUrl();
const UNRESOLVED = '__unresolved__';

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const KINDS = [
  { value: 'site_customer', label: 'Client / Site → Customer' },
  { value: 'service_item', label: 'Service Line → Item' },
  { value: 'auditor_class', label: 'Auditor Roster → Class' },
];

export default function MappingWorkbenchPanel() {
  return (
    <div data-testid="mapping-workbench-panel">
      <RegionRuleCard />
      <Tabs defaultValue="site_customer" className="w-full mt-4">
        <TabsList data-testid="mapping-kind-tabs" className="w-full overflow-x-auto justify-start">
          {KINDS.map((k) => (
            <TabsTrigger key={k.value} value={k.value} data-testid={`mapping-kind-tab-${k.value}`}>{k.label}</TabsTrigger>
          ))}
        </TabsList>
        {KINDS.map((k) => (
          <TabsContent key={k.value} value={k.value} className="mt-4">
            <WorkbenchTable mappingKind={k.value} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function RegionRuleCard() {
  const [rule, setRule] = useState(null);
  useEffect(() => {
    axios.get(`${API}/api/sales-invoicing/region-rule`, { headers: authHeaders() })
      .then(({ data }) => setRule(data))
      .catch(() => {});
  }, []);
  if (!rule) return null;
  const hasUnresolved = (rule.unresolved_auditor_count || 0) > 0;
  // Once every active auditor has an explicit region assigned (nothing
  // left unresolved), this informational banner is no longer actionable
  // — hide it instead of leaving a permanent green notice on screen.
  if (!hasUnresolved) return null;
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-md border text-sm border-amber-200 bg-amber-50"
      data-testid="region-rule-card"
    >
      <MapPin className="h-4 w-4 flex-shrink-0 text-amber-700" />
      <div>
        <Badge className="bg-amber-600 mr-2" data-testid="region-rule-unresolved-badge">{rule.unresolved_auditor_count} auditor(s) unassigned</Badge>
        <span className="text-amber-900">{rule.rule}</span>
        <span className="text-amber-800"> — assign them in Sales Invoicing &gt; Settings.</span>
      </div>
    </div>
  );
}

function WorkbenchTable({ mappingKind }) {
  const [rows, setRows] = useState([]);
  const [referenceOptions, setReferenceOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyUnresolved, setOnlyUnresolved] = useState(false);
  const [historyKey, setHistoryKey] = useState(null);
  const [search, setSearch] = useState('');

  const listType = mappingKind === 'site_customer' ? 'customer' : mappingKind === 'service_item' ? 'item' : 'class';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wb, refs] = await Promise.all([
        axios.get(`${API}/api/sales-invoicing/workbench/${mappingKind}`, { headers: authHeaders() }),
        axios.get(`${API}/api/sales-invoicing/reference-lists/${listType}`, { headers: authHeaders() }),
      ]);
      setRows(wb.data.rows || []);
      setReferenceOptions((refs.data.items || []).filter((i) => i.is_active && i.qbo_status !== 'ambiguous'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load the workbench.');
    } finally {
      setLoading(false);
    }
  }, [mappingKind, listType]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, referenceId) => {
    try {
      await axios.post(`${API}/api/sales-invoicing/workbench/${mappingKind}/decide`, {
        source_key: row.source_key,
        resolved_reference_id: referenceId,
      }, { headers: authHeaders() });
      toast.success(referenceId ? 'Decision saved.' : 'Decision cleared — back to Decision needed.');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save this decision.');
    }
  };

  let visibleRows = onlyUnresolved ? rows.filter((r) => r.status !== 'resolved') : rows;
  if (search.trim()) {
    const q = search.trim().toLowerCase();
    visibleRows = visibleRows.filter((r) => r.source_label.toLowerCase().includes(q));
  }
  const unresolvedCount = rows.filter((r) => r.status !== 'resolved').length;

  return (
    <div data-testid={`workbench-table-${mappingKind}`}>
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div className="text-sm text-slate-500">{rows.length} total — <span className="font-medium text-amber-700">{unresolvedCount} decision needed</span></div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Input
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-0 sm:w-[180px] sm:flex-none"
            data-testid={`workbench-search-${mappingKind}`}
          />
          <Button size="sm" variant="outline" onClick={() => setOnlyUnresolved((v) => !v)} data-testid={`workbench-filter-toggle-${mappingKind}`} className="flex-shrink-0 whitespace-nowrap">
            {onlyUnresolved ? 'Show all' : 'Decision needed only'}
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Source</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Suggested</TableHead>
            <TableHead>QBO target</TableHead>
            <TableHead className="text-right">History</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && <TableRow><TableCell colSpan={5} className="text-center text-slate-500">Loading…</TableCell></TableRow>}
          {!loading && visibleRows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500">Nothing to show.</TableCell></TableRow>}
          {visibleRows.map((row) => (
            <TableRow key={row.source_key} data-testid={`workbench-row-${mappingKind}-${row.source_key}`}>
              <TableCell className="font-medium">{row.source_label}</TableCell>
              <TableCell>
                {row.status === 'resolved' && (
                  <Badge className="bg-emerald-600" data-testid={`workbench-status-${row.source_key}`}><CheckCircle2 className="h-3 w-3 mr-1" />Resolved</Badge>
                )}
                {row.status !== 'resolved' && row.resolved_reference_id && (row.target_ambiguous || row.target_inactive) && (
                  <Badge className="bg-orange-500" data-testid={`workbench-status-${row.source_key}`} title={row.target_ambiguous ? 'The saved target now has a code conflict from a later import — resolve it in the reference list.' : 'The saved target has since been deactivated or removed from the reference list.'}>
                    <CircleDashed className="h-3 w-3 mr-1" />Decision needed ({row.target_ambiguous ? 'target ambiguous' : 'target inactive'})
                  </Badge>
                )}
                {row.status !== 'resolved' && (!row.resolved_reference_id || (!row.target_ambiguous && !row.target_inactive)) && (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800" data-testid={`workbench-status-${row.source_key}`}><CircleDashed className="h-3 w-3 mr-1" />Decision needed</Badge>
                )}
              </TableCell>
              <TableCell className="text-sm text-slate-500">
                {row.suggested ? `${row.suggested.reference_name} (${Math.round(row.suggested.confidence * 100)}%)` : '—'}
              </TableCell>
              <TableCell>
                <Select
                  value={row.resolved_reference_id ? String(row.resolved_reference_id) : UNRESOLVED}
                  onValueChange={(v) => decide(row, v === UNRESOLVED ? null : parseInt(v, 10))}
                >
                  <SelectTrigger className="w-[220px]" data-testid={`workbench-select-${row.source_key}`}>
                    <SelectValue placeholder="Decision needed">
                      {row.target_deleted
                        ? '(deleted target)'
                        : row.target_ambiguous
                          ? `${row.resolved_reference_name} (ambiguous)`
                          : row.target_inactive
                            ? `${row.resolved_reference_name} (inactive)`
                            : undefined}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNRESOLVED}>— Decision needed —</SelectItem>
                    {row.suggested && (
                      <SelectItem value={String(row.suggested.reference_id)}>
                        Suggested: {row.suggested.reference_name}
                      </SelectItem>
                    )}
                    {(row.target_inactive || row.target_ambiguous) && row.resolved_reference_id && (
                      <SelectItem value={String(row.resolved_reference_id)}>
                        {row.target_deleted
                          ? '(deleted target)'
                          : row.target_ambiguous
                            ? `${row.resolved_reference_name} (ambiguous — resolve in Reference Lists first)`
                            : `${row.resolved_reference_name} (inactive — pick a different target)`}
                      </SelectItem>
                    )}
                    {referenceOptions.filter((o) => !row.suggested || o.id !== row.suggested.reference_id).map((opt) => (
                      <SelectItem key={opt.id} value={String(opt.id)}>{opt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell className="text-right">
                <Button size="icon" variant="ghost" onClick={() => setHistoryKey(row.source_key)} data-testid={`workbench-history-btn-${row.source_key}`}>
                  <History className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
      {historyKey && (
        <ChangeHistoryDialog mappingKind={mappingKind} sourceKey={historyKey} onClose={() => setHistoryKey(null)} />
      )}
    </div>
  );
}
